import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { SearchNavBar } from "../../components/ui";
import { AppStrings, getCurrentIP, logApiErrorToSentry, removeKeys } from "../../utils";
import CalculationSection from "./CalculationSection";
import ReviewOverview from "./ReviewOverview";
import { remittanceActions } from "../../state/remittanceSlice";
import Typography from "../../../../components/ui/Typography";
import { ConsentModal, TermsAndCondition, RemitterEsignModal } from "../../components/modal";
import download from "downloadjs";
import { remittanceApi } from "../../api/remittanceApi";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

const RemittanceReview: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient, review, documents } = useAppSelector(
    (state) => state.remittance
  );
  const { amountCalculation } = calculator;
  const { selectedRemitter } = remitter;
  const { selectedRecipient } = recipient;
  const { remitterSignedStatus } = review;

  const loading = review?.remitterSignedStatus?.loading || review?.consentData?.loading;
  const error = review?.remitterSignedStatus?.error || review?.consentData?.error;

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    dispatch(
      remittanceActions.fetchRemitterSignedStatusRequested(remitter?.selectedRemitter?.userKey)
    );
    dispatch(remittanceActions.fetchGetModalContentRequested("KNOW_MORE"));
    getIpData();
  }, []);

  const getIpData = async () => {
    const currentIP = await getCurrentIP();
    dispatch(remittanceActions.setReviewDetailsInput({ currentIP }));
  };

  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 5) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  {
    /* getTransactionSummary */
  }
  useEffect(() => {
    const isSend = calculator?.amountCalculation?.changeInputFrom === "send";

    const amountCalculation = { ...calculator?.amountCalculation };
    const isUserSendORRecipient =
      isSend && !calculator?.amountCalculation?.lock_reciepient_recieve
        ? "reciepient_recieve"
        : "user_send";
    const keys = [
      isUserSendORRecipient,
      "changeInputFrom",
      "countryDropdown",
      "purposeDropdown",
      "vendorDropdown",
      "lock_reciepient_recieve",
      "skipApi",
    ];
    const formData = removeKeys(amountCalculation, keys);
    formData.declare_amount = Number(formData.declare_amount) || 0;
    formData.userId = remitter?.selectedRemitter?.userKey;

    const debounce = setTimeout(() => {
      dispatch(remittanceActions.fetchTransactionSummaryRequested(formData));
    }, 0);
  }, []);

  const viewORDownloadA2Form = async (type: any) => {
    try {
      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: true, error: null }));
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: true, error: null }));
      }

      const totalYouPay = calculator?.amountCalculation?.roundOff
        ? calculator?.getTransactionSummary?.data?.totalPayInCeils?.find(
            (item: any) => item.id === 10
          )?.total_pay
        : calculator?.getTransactionSummary?.data?.total_pay;
      const params = new URLSearchParams({
        city: selectedRemitter?.city ?? "",
        dest_curr: amountCalculation?.dest_currency ?? "",
        pan_num: amountCalculation?.pan_number ?? "",
        pincode: selectedRemitter?.pinCode ?? "",
        purpose_code: amountCalculation?.purposeCode ?? "",
        purpose_description: amountCalculation?.transaction_purpose ?? "",
        reciepient_recieve: `${calculator?.getTransactionSummary?.data?.reciepient_recieve ?? ""}`,
        recipeint_accnt_num: selectedRecipient?.bankAccountNumber ?? "",
        recipient_bank_name: selectedRecipient?.bankName ?? "",
        recipient_first_name: selectedRecipient?.firstName ?? "",
        recipient_last_name: selectedRecipient?.lastName ?? "",
        reicpient_bank_accnt_country: selectedRecipient?.bankAccountCountry ?? "",
        state: selectedRecipient?.bankAccountCountry ?? "",
        total_pay: `${totalYouPay ?? ""}`,
        user_address1: encodeURIComponent(selectedRemitter?.addressLine1) ?? "",
        user_first_name: selectedRemitter?.firstName ?? "",
        user_last_name: selectedRemitter?.lastName ?? "",
        recipientId: selectedRecipient?.id ?? "",
        bene: amountCalculation?.isBene ? "true" : "false",
      });

      const response = await remittanceApi.getViewA2form(
        remitter?.selectedRemitter?.userKey ?? "",
        params
      );

      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: false, error: null }));
        const file = new Blob([response.data], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: false, error: null }));
        const content = response.headers["content-type"];
        download(response.data, "download_A2.pdf", content);
      }
    } catch (error: any) {
      let message = "An unexpected error occurred in Viewing A2.";
      if (error?.response?.data?.error?.type) {
        message = error.response.data.error.type;
      }
      if (type == "view") {
        dispatch(remittanceActions.setViewA2FormState({ loading: false, error: null }));
      } else {
        dispatch(remittanceActions.setDowanloadA2FormState({ loading: false, error: null }));
      }
      toast.error(message);
      logApiErrorToSentry(error, "viewORDownloadA2Form");
    }
  };

  const initializeTransaction = async () => {
    try {
      dispatch(remittanceActions.setTransactionDetail({ data: null, error: null, loading: true }));
      const formData = {
        transVendorKey: amountCalculation?.vendorDropdown?.userId,
        advance_pay: 0,
        base_currency: amountCalculation?.base_currency,
        cgst: calculator?.getTransactionSummary?.data?.cgst,
        conversion_rate: calculator?.getTransactionSummary?.data?.conversion_rate,
        dest_currency: calculator?.getTransactionSummary?.data?.dest_currency,
        hop_fees: calculator?.getTransactionSummary?.data?.hop_fees,
        igst_fees: calculator?.getTransactionSummary?.data?.igst_fee,
        reciepient_recieve: calculator?.getTransactionSummary?.data?.reciepient_recieve,
        service_tax: calculator?.getTransactionSummary?.data?.service_tax,
        sgst: calculator?.getTransactionSummary?.data?.sgst,
        tcs: calculator?.getTransactionSummary?.data?.tcs,
        total_pay: calculator?.getTransactionSummary?.data?.total_pay,
        total_tax: calculator?.getTransactionSummary?.data?.total_tax,
        mgr_initial: calculator?.getTransactionSummary?.data?.mgr_initial,
        transaction_purpose: calculator?.getTransactionSummary?.data?.transaction_purpose,
        purposeCode: calculator?.getTransactionSummary?.data?.purposeCode,
        transactionLeadIds: documents?.recipientLeadDocs.flatMap((i) => i.leadId ?? []),
        isA2FormConsent: true,
        user_send: calculator?.getTransactionSummary?.data?.user_send,
        isAdvancePay: false,
        isFullPay: true,
        hop_coins: calculator?.getTransactionSummary?.data?.hop_coins,
        isEducation: amountCalculation?.isEducation,
        id_recipient: recipient?.selectedRecipient?.id + "",
        conversion_amount: calculator?.getTransactionSummary?.data?.conversion_amount,
        nostro_charges: calculator?.getTransactionSummary?.data?.nostro_charges,
        nostro_charges_new: calculator?.getTransactionSummary?.data?.nostro_charges_new,
        promo_code: calculator?.getTransactionSummary?.data?.promo_code,
        promo_code_disount: calculator?.getTransactionSummary?.data?.promo_code_discount,
        vendor_margin: calculator?.getTransactionSummary?.data?.vendor_margin,
        vendor_key: authState?.currentUser?.vendor_key,
        referrerVendor: amountCalculation?.vendorDropdown?.vendorCode ?? "",
        isBene: amountCalculation?.isBene,
        ipAddress: review?.reviewDetails?.currentIP,
        sourceOfFunds: review?.reviewDetails?.sourceOfFundsDropdown?.label,
        trxnDeclarations: {
          vendorCode: amountCalculation?.vendorDropdown?.vendorCode ?? "",
          lanNumber: review?.reviewDetails?.lanNumber ?? "",
          paymentUtr: review?.reviewDetails?.utrNumber ?? "",
        },
        nextDecimal: amountCalculation?.roundOff ? 10 : 0,
        consent: [
          {
            store: true,
            accepted: review?.reviewDetails?.consentForm,
            pageName: "COMPLIANCE",
            ipAddress: review?.reviewDetails?.currentIP,
          },
        ],
        ...(remitter?.selectedQuoteUniqueKey
          ? { quote_unique_key: remitter.selectedQuoteUniqueKey }
          : {}),
      };
      MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Review_Submit, {
        authState: authState,
        remitterState: remitterState,
        formData: formData,
      });
      const response = await remittanceApi.initializeTransaction(
        remitter?.selectedRemitter?.userKey,
        formData
      );
      if (response?.data) {
        dispatch(remittanceActions.setActiveStepNumber(6));
        dispatch(
          remittanceActions.setTransactionDetail({
            data: { ...response?.data, remitterId: remitter?.selectedRemitter?.userKey },
            error: null,
            loading: false,
          })
        );
        if (response?.data?.transactionNumber) {
          MixpanelConfig.trackEvent(response?.data?.transactionNumber, {
            authState: authState,
            remitterState: remitterState,
            formData: formData,
            transactionNumber: response?.data?.transactionNumber,
            id_transaction: response?.data?.id_transaction,
            from: "NEW_STATE_MANAGEMENT",
          });
        }
        navigate("/send/payment");
      } else {
        toast.error(AppStrings.GENERIC_ERROR);
        dispatch(
          remittanceActions.setTransactionDetail({ data: null, error: null, loading: false })
        );
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setTransactionDetail({ data: null, error: null, loading: false }));
      logApiErrorToSentry(err, "initializeTransaction");
    }
  };

  return (
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px] scale-90 -mt-10">
        <ToastContainer autoClose={3000} position="top-right" />

        <SearchNavBar
          title={AppStrings.REVIEW_SCREEN.REVIEW_CALCULATION}
          addButtonTitle={AppStrings.BUTTON.SUBMIT}
          titleAndButton={false}
          clickButton={false}
        />

        {error && (
          <div className="p-4 flex justify-center items-center">
            <Typography tagType="div" text={error} className="text-sm font-normal text-red-100" />
          </div>
        )}

        <div className="flex justify-center">
          <CalculationSection />
          <div className="w-1 h-[580px] border-4 border-r mx-10" />
          <ReviewOverview
            viewORDownloadA2Form={viewORDownloadA2Form}
            initializeTransaction={initializeTransaction}
          />
        </div>
      </div>

      <TermsAndCondition
        open={remitterState?.modalType === "TERMS_AND_CONDITIONS"}
        onYes={() => {
          dispatch(remittanceActions.setModalType(""));
          dispatch(remittanceActions.setReviewDetailsInput({ termsAndConditions: true }));
        }}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
      />

      <ConsentModal
        open={remitterState?.modalType === "CONSENT_FORM"}
        onNo={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
        contentData={review?.consentData?.data}
      />

      <RemitterEsignModal
        handleA2Form={viewORDownloadA2Form}
        visible={remitterState?.modalType == "REMITTER_ESIGN_MODAL"}
        a2Link={review?.remitterSignedStatus?.data?.link ?? ""}
        onClose={() => {
          dispatch(remittanceActions.setModalType(""));
          dispatch(
            remittanceActions.fetchRemitterSignedStatusRequested(
              remitter?.selectedRemitter?.userKey
            )
          );
        }}
      />
    </>
  );
};

export default RemittanceReview;
